import React, { useEffect, useState } from "react";
import { Link, Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, BookOpen, ShoppingBag, Newspaper, MessageSquare, Plus, Trash2, Edit3, X, CheckCircle2, Image } from "lucide-react";
import { toast } from "sonner";
import api, { formatNGN } from "../lib/api";
import FileUploader from "../components/FileUploader";
import AdminGallery from "./AdminGallery";

export default function AdminDashboard() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-slate-900/10 max-w-7xl mx-auto my-10 px-4 sm:px-6 lg:px-8" data-testid="page-admin">
            <aside className="md:col-span-3 bg-[#0F172A] text-[#faf9f6] p-6 space-y-1 rounded-sm">
                <div className="font-mono-cap text-xs text-[#c5a059] mb-4">Admin · Chamber Console</div>
                {[
                    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
                    { to: "/admin/users", label: "Users", icon: Users },
                    { to: "/admin/publications", label: "Publications", icon: BookOpen },
                    { to: "/admin/gallery", label: "Gallery", icon: Image },
                    { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
                    { to: "/admin/blog", label: "Insights", icon: Newspaper },
                    { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
                ].map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        data-testid={`admin-nav-${l.label.toLowerCase()}`}
                        className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                                isActive ? "bg-[#c5a059] text-[#0F172A]" : "text-[#faf9f6]/80 hover:bg-[#faf9f6]/10"
                            }`
                        }
                    >
                        <l.icon className="w-4 h-4" /> {l.label}
                    </NavLink>
                ))}
            </aside>

            <div className="md:col-span-9 bg-[#faf9f6] p-6 lg:p-10">
                <Routes>
                    <Route index element={<Overview />} />
                    <Route path="users" element={<UsersPanel />} />
                    <Route path="publications" element={<PublicationsPanel />} />
                    <Route path="gallery" element={<AdminGallery />} />
                    <Route path="orders" element={<OrdersPanel />} />
                    <Route path="blog" element={<BlogPanel />} />
                    <Route path="inquiries" element={<InquiriesPanel />} />
                </Routes>
            </div>
        </div>
    );
}

function Overview() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
    if (!s) return <div className="font-mono-cap text-xs text-slate-500">Loading…</div>;
    const cards = [
        { label: "Total Revenue", value: formatNGN(s.revenue_ngn), tone: "navy" },
        { label: "Paid Orders", value: s.orders_paid, tone: "gold" },
        { label: "Total Orders", value: s.orders_total },
        { label: "Publications", value: s.publications },
        { label: "Users", value: s.users },
        { label: "Insights", value: s.blog_posts },
        { label: "Inquiries", value: s.inquiries },
    ];
    return (
        <div data-testid="admin-overview">
            <div className="font-mono-cap text-xs text-[#c5a059] mb-3">Overview</div>
            <h1 className="font-serif-display text-3xl md:text-4xl mb-8">Chamber at a glance.</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {cards.map((c) => (
                    <div key={c.label} className={`p-5 border border-slate-900/10 ${c.tone === "navy" ? "bg-[#0F172A] text-[#faf9f6]" : c.tone === "gold" ? "bg-[#c5a059] text-[#0F172A]" : "bg-white text-slate-900"}`}
                         data-testid={`admin-kpi-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                        <div className="font-mono-cap text-[10px] opacity-70">{c.label}</div>
                        <div className="font-serif-display text-2xl md:text-3xl mt-2">{c.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function UsersPanel() {
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const load = () => api.get("/users").then(({ data }) => setUsers(data));
    useEffect(() => { load(); }, []);
    const changeRole = async (u, role) => {
        try { await api.put(`/users/${u.user_id}/role`, null, { params: { role } }); toast.success("Role updated"); load(); }
        catch { toast.error("Could not update role"); }
    };
    const remove = async (u) => {
        if (!window.confirm(`Delete ${u.email}?`)) return;
        try { await api.delete(`/users/${u.user_id}`); toast.success("User removed"); load(); }
        catch { toast.error("Could not delete"); }
    };
    return (
        <div data-testid="admin-users">
            <PanelHead title="Users" action={
                <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-[#0F172A] text-[#faf9f6] px-4 py-2 rounded-sm text-sm hover:bg-[#c5a059] hover:text-[#0F172A]" data-testid="admin-user-new"><Plus className="w-4 h-4" /> New user</button>
            } />
            <div className="overflow-x-auto border border-slate-900/10 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-[#f3f1ea] text-left">
                        <tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Role</Th><Th>Joined</Th><Th></Th></tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.user_id} className="border-t border-slate-900/10" data-testid={`admin-user-${u.user_id}`}>
                                <Td>{u.name}</Td>
                                <Td>{u.email}</Td>
                                <Td>{u.phone || "—"}</Td>
                                <Td>
                                    <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="border border-slate-900/15 rounded-sm px-2 py-1 bg-white" data-testid={`admin-user-role-${u.user_id}`}>
                                        <option value="user">user</option>
                                        <option value="lawyer">lawyer</option>
                                        <option value="admin">admin</option>
                                    </select>
                                </Td>
                                <Td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</Td>
                                <Td>
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditing(u)} className="text-slate-600 hover:text-[#c5a059]" data-testid={`admin-user-edit-${u.user_id}`}><Edit3 className="w-4 h-4" /></button>
                                        <button onClick={() => remove(u)} className="text-red-600 hover:text-red-800" data-testid={`admin-user-delete-${u.user_id}`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {creating && <UserCreator onClose={() => { setCreating(false); load(); }} />}
            {editing && <UserEditor user={editing} onClose={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function UserCreator({ onClose }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", role: "user", password: "" });
    const submit = async (e) => {
        e.preventDefault();
        try { await api.post("/users", form); toast.success("User created"); onClose(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Could not create user"); }
    };
    return (
        <Modal title="New user" onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid="admin-user-create-form">
                <TextInput label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                <TextInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                <TextInput label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <label className="block">
                    <span className="font-mono-cap text-[11px] text-slate-700 block mb-2">Role *</span>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white" data-testid="admin-user-create-role">
                        <option value="user">user</option>
                        <option value="lawyer">lawyer</option>
                        <option value="admin">admin</option>
                    </select>
                </label>
                <TextInput label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />
                <button type="submit" className="bg-[#0F172A] text-[#faf9f6] px-5 py-2.5 rounded-sm" data-testid="admin-user-create-save">Create user</button>
            </form>
        </Modal>
    );
}

function UserEditor({ user, onClose }) {
    const [form, setForm] = useState({ name: user.name || "", email: user.email || "", phone: user.phone || "", role: user.role || "user", password: "" });
    const submit = async (e) => {
        e.preventDefault();
        const body = { name: form.name, email: form.email, phone: form.phone, role: form.role };
        if (form.password) body.password = form.password;
        try { await api.put(`/users/${user.user_id}`, body); toast.success("Updated"); onClose(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    };
    return (
        <Modal title={`Edit ${user.name}`} onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid="admin-user-edit-form">
                <TextInput label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                <TextInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                <TextInput label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <label className="block">
                    <span className="font-mono-cap text-[11px] text-slate-700 block mb-2">Role *</span>
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white" data-testid="admin-user-edit-role">
                        <option value="user">user</option>
                        <option value="lawyer">lawyer</option>
                        <option value="admin">admin</option>
                    </select>
                </label>
                <TextInput label="Reset password (optional)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
                <button type="submit" className="bg-[#0F172A] text-[#faf9f6] px-5 py-2.5 rounded-sm" data-testid="admin-user-edit-save">Save changes</button>
            </form>
        </Modal>
    );
}

function PublicationsPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/publications").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const remove = async (p) => {
        if (!window.confirm(`Delete ${p.title}?`)) return;
        try { await api.delete(`/publications/${p.id}`); toast.success("Deleted"); load(); } catch { toast.error("Could not delete"); }
    };
    return (
        <div data-testid="admin-publications">
            <PanelHead title="Publications" action={
                <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#0F172A] text-[#faf9f6] px-4 py-2 rounded-sm text-sm hover:bg-[#c5a059] hover:text-[#0F172A]" data-testid="admin-pub-new"><Plus className="w-4 h-4" /> New</button>
            } />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {items.map((p) => (
                    <div key={p.id} className="flex gap-4 border border-slate-900/10 bg-white p-4" data-testid={`admin-pub-${p.id}`}>
                        <img src={p.cover_url} alt={p.title} className="w-20 h-28 object-cover" />
                        <div className="flex-1">
                            <div className="font-serif-display text-lg">{p.title}</div>
                            <div className="text-xs text-slate-500 mt-1">{p.category} · {formatNGN(p.price_ngn)}</div>
                            <div className="mt-3 flex gap-2">
                                <button onClick={() => setEditing(p)} className="inline-flex items-center gap-1 text-xs border border-slate-900/15 px-3 py-1 rounded-sm hover:border-[#c5a059]" data-testid={`admin-pub-edit-${p.id}`}><Edit3 className="w-3 h-3"/> Edit</button>
                                <button onClick={() => remove(p)} className="inline-flex items-center gap-1 text-xs text-red-700 border border-red-200 px-3 py-1 rounded-sm" data-testid={`admin-pub-delete-${p.id}`}><Trash2 className="w-3 h-3"/> Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {editing && <PublicationEditor pub={editing} onClose={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function PublicationEditor({ pub, onClose }) {
    const isNew = !pub.id;
    const [form, setForm] = useState({
        title: pub.title || "", author: pub.author || "Rotimi Jacobs, SAN, CON", description: pub.description || "",
        price_ngn: pub.price_ngn || 50000, cover_url: pub.cover_url || "", category: pub.category || "Law",
        formats: pub.formats || ["hardcopy", "softcopy"], stock: pub.stock ?? 100, pdf_path: pub.pdf_path || "",
    });
    const submit = async (e) => {
        e.preventDefault();
        try {
            if (isNew) await api.post("/publications", { ...form, price_ngn: Number(form.price_ngn), stock: Number(form.stock) });
            else await api.put(`/publications/${pub.id}`, { ...form, price_ngn: Number(form.price_ngn), stock: Number(form.stock) });
            toast.success(isNew ? "Created" : "Updated");
            onClose();
        } catch { toast.error("Save failed"); }
    };
    return (
        <Modal title={isNew ? "New publication" : "Edit publication"} onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid="admin-pub-form">
                <TextInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
                <TextInput label="Author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} required />
                <div className="space-y-2">
                    <TextInput label="Cover image URL" value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} required />
                    <FileUploader
                        kind="image"
                        value={form.cover_url}
                        onUploaded={(d) => setForm({ ...form, cover_url: d.public_url })}
                        testidPrefix="admin-pub-cover"
                    />
                </div>
                <div className="space-y-2">
                    <label className="block">
                        <span className="font-mono-cap text-[10px] text-slate-500 block mb-2">Softcopy PDF (storage path)</span>
                        <input value={form.pdf_path} onChange={(e) => setForm({ ...form, pdf_path: e.target.value })} placeholder="auto-filled after upload" className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white" readOnly />
                    </label>
                    <FileUploader
                        kind="pdf"
                        value={form.pdf_path}
                        onUploaded={(d) => setForm({ ...form, pdf_path: d.storage_path })}
                        testidPrefix="admin-pub-pdf"
                    />
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <TextInput label="Price (NGN)" type="number" value={form.price_ngn} onChange={(v) => setForm({ ...form, price_ngn: v })} required />
                    <TextInput label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} required />
                    <TextInput label="Stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} required />
                </div>
                <label className="block">
                    <span className="font-mono-cap text-[10px] text-slate-500 block mb-2">Description *</span>
                    <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} required className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white" />
                </label>
                <button type="submit" className="bg-[#0F172A] text-[#faf9f6] px-5 py-2.5 rounded-sm" data-testid="admin-pub-save">Save</button>
            </form>
        </Modal>
    );
}

function OrdersPanel() {
    const [orders, setOrders] = useState([]);
    const load = () => api.get("/orders").then(({ data }) => setOrders(data));
    useEffect(() => { load(); }, []);
    const confirmPayment = async (o) => {
        if (!window.confirm(`Mark order ${o.payment_ref} as paid?`)) return;
        try { await api.post(`/orders/${o.id}/confirm`); toast.success("Payment confirmed"); load(); }
        catch { toast.error("Could not confirm"); }
    };
    return (
        <div data-testid="admin-orders">
            <PanelHead title="Orders" />
            <div className="overflow-x-auto border border-slate-900/10 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-[#f3f1ea] text-left"><tr><Th>Date</Th><Th>Reference</Th><Th>Customer</Th><Th>Method</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th><Th></Th></tr></thead>
                    <tbody>
                        {orders.length === 0 && (<tr><td className="p-6 text-center text-slate-500" colSpan={7}>No orders yet.</td></tr>)}
                        {orders.map((o) => (
                            <tr key={o.id} className="border-t border-slate-900/10" data-testid={`admin-order-${o.id}`}>
                                <Td>{new Date(o.created_at).toLocaleString()}</Td>
                                <Td><code className="text-xs">{o.payment_ref}</code></Td>
                                <Td>{o.customer_name}<br /><span className="text-xs text-slate-600">{o.customer_email}</span><br /><span className="text-xs text-slate-600">{o.customer_phone}</span></Td>
                                <Td className="text-sm uppercase tracking-[0.18em] text-slate-600">{o.payment_method || 'paystack'}</Td>
                                <Td>{o.items.length}</Td>
                                <Td>{formatNGN(o.total_ngn)}</Td>
                                <Td><span className={`font-mono-cap text-[11px] px-2 py-1 rounded-sm ${o.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{o.status}</span></Td>
                                <Td>
                                    {o.status !== "paid" && (
                                        <button
                                            onClick={() => confirmPayment(o)}
                                            data-testid={`admin-order-confirm-${o.id}`}
                                            className="inline-flex items-center gap-1 bg-emerald-700 text-white text-xs px-3 py-1.5 rounded-sm hover:bg-emerald-800"
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Confirm payment
                                        </button>
                                    )}
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function BlogPanel() {
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/blog").then(({ data }) => setPosts(data));
    useEffect(() => { load(); }, []);
    const remove = async (p) => {
        if (!window.confirm(`Delete ${p.title}?`)) return;
        try { await api.delete(`/blog/${p.id}`); toast.success("Deleted"); load(); } catch { toast.error("Could not delete"); }
    };
    return (
        <div data-testid="admin-blog">
            <PanelHead title="Insights" action={
                <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#0F172A] text-[#faf9f6] px-4 py-2 rounded-sm text-sm" data-testid="admin-blog-new"><Plus className="w-4 h-4" /> New post</button>
            } />
            <div className="space-y-3">
                {posts.map((p) => (
                    <div key={p.id} className="border border-slate-900/10 bg-white p-4 flex items-center justify-between" data-testid={`admin-blog-${p.id}`}>
                        <div>
                            <div className="font-serif-display text-lg">{p.title}</div>
                            <div className="text-xs text-slate-500 mt-1">{p.category} · {new Date(p.created_at).toLocaleDateString()}</div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setEditing(p)} className="inline-flex items-center gap-1 text-xs border border-slate-900/15 px-3 py-1 rounded-sm" data-testid={`admin-blog-edit-${p.id}`}><Edit3 className="w-3 h-3"/> Edit</button>
                            <button onClick={() => remove(p)} className="inline-flex items-center gap-1 text-xs text-red-700 border border-red-200 px-3 py-1 rounded-sm" data-testid={`admin-blog-delete-${p.id}`}><Trash2 className="w-3 h-3"/> Delete</button>
                        </div>
                    </div>
                ))}
            </div>
            {editing && <BlogEditor post={editing} onClose={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function BlogEditor({ post, onClose }) {
    const isNew = !post.id;
    const [form, setForm] = useState({
        title: post.title || "", slug: post.slug || "", category: post.category || "Law",
        excerpt: post.excerpt || "", content: post.content || "", cover_url: post.cover_url || "",
        author: post.author || "Rotimi Jacobs, SAN",
    });
    const submit = async (e) => {
        e.preventDefault();
        try {
            if (isNew) await api.post("/blog", form);
            else await api.put(`/blog/${post.id}`, form);
            toast.success(isNew ? "Created" : "Updated");
            onClose();
        } catch { toast.error("Save failed"); }
    };
    return (
        <Modal title={isNew ? "New post" : "Edit post"} onClose={onClose}>
            <form onSubmit={submit} className="space-y-4" data-testid="admin-blog-form">
                <TextInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
                <div className="grid grid-cols-2 gap-3">
                    <TextInput label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} required />
                    <TextInput label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} required />
                </div>
                <TextInput label="Author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} required />
                <TextInput label="Cover image URL" value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} />
                <label className="block">
                    <span className="font-mono-cap text-[10px] text-slate-500 block mb-2">Excerpt *</span>
                    <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} required className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white" />
                </label>
                <label className="block">
                    <span className="font-mono-cap text-[10px] text-slate-500 block mb-2">Content *</span>
                    <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} required className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white" />
                </label>
                <button type="submit" className="bg-[#0F172A] text-[#faf9f6] px-5 py-2.5 rounded-sm" data-testid="admin-blog-save">Save</button>
            </form>
        </Modal>
    );
}

function InquiriesPanel() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/inquiries").then(({ data }) => setItems(data)); }, []);
    return (
        <div data-testid="admin-inquiries">
            <PanelHead title="Client Inquiries" />
            <div className="space-y-3">
                {items.length === 0 && <div className="text-sm text-slate-500">No inquiries yet.</div>}
                {items.map((i) => (
                    <div key={i.id} className="border border-slate-900/10 bg-white p-5" data-testid={`admin-inq-${i.id}`}>
                        <div className="flex items-center justify-between">
                            <div className="font-serif-display text-lg">{i.subject}</div>
                            <div className="font-mono-cap text-[10px] text-slate-500">{new Date(i.created_at).toLocaleString()}</div>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{i.name} · {i.email} {i.phone && `· ${i.phone}`}</div>
                        <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{i.message}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

const PanelHead = ({ title, action }) => (
    <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-display text-2xl md:text-3xl">{title}</h1>
        {action}
    </div>
);
const Th = ({ children }) => <th className="px-4 py-3 font-mono-cap text-[10px] text-slate-500">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;
const TextInput = ({ label, value, onChange, type = "text", required }) => (
    <label className="block">
        <span className="font-mono-cap text-[10px] text-slate-500 block mb-2">{label}{required && " *"}</span>
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full px-4 py-3 border border-slate-900/15 rounded-sm bg-white" />
    </label>
);
const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-[#faf9f6] w-full max-w-2xl border border-slate-900/10 rounded-sm max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-900/10 sticky top-0 bg-[#faf9f6]">
                <div className="font-serif-display text-xl">{title}</div>
                <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="modal-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>
);
